import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-otp',
  standalone: true,
  imports: [RouterModule, ReactiveFormsModule],
  templateUrl: './otp.component.html',
  styleUrl: './otp.component.css',
})
export class OtpComponent implements OnInit, OnDestroy {
  otpForm: FormGroup;
  timer: number = 300;
  interval: any;
  resendCooldown: number = 30;
  resendInterval: any;
  identifier: string = '';
  maskedMobileNumber: string = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
  ) {
    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp2: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp3: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp4: ['', [Validators.required, Validators.pattern('[0-9]')]],
    });
  }

  ngOnInit(): void {
    this.startTimer();
    this.startResendCooldown();

    const identifierFromStorage =
      localStorage.getItem('forgot_password_identifier') ||
      localStorage.getItem('email');

    if (identifierFromStorage) {
      this.identifier = identifierFromStorage;
      this.maskedMobileNumber =
        localStorage.getItem('forgot_password_mobile_number') ||
        'your registered phone number';
    } else {
      this.toastr.error('Contact details not found. Please try again.', 'Error');
      this.router.navigate(['/forgot-password']);
    }
  }

  startTimer(): void {
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        clearInterval(this.interval);
      }
    }, 1000);
  }

  startResendCooldown(): void {
    clearInterval(this.resendInterval);
    this.resendCooldown = 30;
    this.resendInterval = setInterval(() => {
      if (this.resendCooldown > 0) {
        this.resendCooldown--;
      } else {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  moveToNext(event: any, nextInput: string): void {
    if (event.target.value.length === 1) {
      const nextElement = document.getElementById(nextInput);
      if (nextElement) {
        nextElement.focus();
      }
    }
  }

  handleOtpBackspace(event: KeyboardEvent, currentControl: string, previousInputId?: string): void {
    if (event.key !== 'Backspace') {
      return;
    }

    const currentValue = this.otpForm.get(currentControl)?.value;
    if (currentValue) {
      return;
    }

    if (previousInputId) {
      this.otpForm.get(previousInputId)?.setValue('');

      const previousElement = document.getElementById(previousInputId);
      if (previousElement) {
        previousElement.focus();
      }
    }
  }

  resendOtp(): void {
    if (!this.canResendOtp) {
      return;
    }

    this.toastr.info('Resending OTP...');
    this.timer = 300;
    this.startTimer();
    this.startResendCooldown();
    this.authService.forgotPassword(this.identifier, 'email').subscribe({
      next: (response) => {
        const mobileNumber = response?.data?.mobile_number;
        if (mobileNumber) {
          this.maskedMobileNumber = mobileNumber;
          localStorage.setItem('forgot_password_mobile_number', mobileNumber);
        }
        const successMessage = response?.message_key || response?.status;
        if (successMessage) {
          this.toastr.success(successMessage, 'Success');
        }
      },
      error: (error) => {
        const errorMessage =
          error?.error?.message_key ||
          error?.error?.status ||
          'Something went wrong.';
        this.toastr.error(errorMessage, 'Error');
      },
      });
  }

  onSubmit(): void {
    if (this.otpForm.valid) {
      const otp = `${this.otpForm.value.otp1}${this.otpForm.value.otp2}${this.otpForm.value.otp3}${this.otpForm.value.otp4}`;
      this.authService.verifyOtp(this.identifier, otp, 'email').subscribe({
        next: (response) => {
          if (response.status === 'Operation successful') {
            const successMessage = response?.message_key || response?.status;
            if (successMessage) {
              this.toastr.success(successMessage, 'Success');
            }
            this.router.navigate(['/reset-password']);
          }
        },
        error: (error) => {
          const errorMessage =
            error?.error?.message_key ||
            error?.error?.status ||
            'Something went wrong.';
          this.toastr.error(errorMessage, 'Error');
        },
        });
    }
  }

  get formattedTime(): string {
    const minutes = Math.floor(this.timer / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (this.timer % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  get canResendOtp(): boolean {
    return this.resendCooldown === 0;
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
    clearInterval(this.resendInterval);
  }
}
